import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { IconButtonProps } from '../../element/Button/IconButton';
import { IconButtonSize } from '../../element/Button/IconButton';

import { GroupedElementsItem } from './GroupedElementsItem';

export enum GroupedElementsShape {
  SQUARE = 'square',
  PILL = 'pill',
}

export type GroupedElementsProps = {
  children: React.ReactNode;
  direction?: 'horizontal' | 'vertical';
  fluid?: boolean;
  name?: string;
  disableHover?: boolean;
  outlined?: boolean;
  shadow?: boolean;
  current?: boolean;
  divider?: boolean;
  shape?: GroupedElementsShape | `${GroupedElementsShape}`;
  size?: IconButtonProps['size'];
};

// A host element is left alone: `size` is a real attribute on a few of them.
const asSegment = (
  children: React.ReactNode,
  size: NonNullable<IconButtonProps['size']>
): React.ReactNode =>
  React.Children.map(children, child => {
    if (!React.isValidElement(child) || typeof child.type === 'string') {
      return child;
    }

    const sized = child as React.ReactElement<{
      size?: IconButtonProps['size'];
    }>;

    return sized.props.size === undefined
      ? React.cloneElement(sized, { size })
      : child;
  });

// A fragment is looked through, or everything inside it would become one segment.
const asItems = (
  children: React.ReactNode,
  size: NonNullable<IconButtonProps['size']>
): React.ReactNode =>
  React.Children.map(children, child => {
    if (!React.isValidElement(child)) {
      // `null`, `false` and '' stay absent; text and numbers (including 0) are segments.
      const isText =
        (typeof child === 'string' && child !== '') ||
        typeof child === 'number';

      return isText ? (
        <GroupedElementsItem>{child}</GroupedElementsItem>
      ) : (
        child
      );
    }

    const props = child.props as {
      children?: React.ReactNode;
      size?: IconButtonProps['size'];
    };

    if (child.type === GroupedElementsItem) {
      return React.cloneElement(child, {}, asSegment(props.children, size));
    }

    return child.type === React.Fragment ? (
      asItems(props.children, size)
    ) : (
      <GroupedElementsItem>{asSegment(child, size)}</GroupedElementsItem>
    );
  });

/** Several controls drawn as one: the group joins the items and rounds the ends. */
export const GroupedElements = ({
  children,
  direction = 'horizontal',
  disableHover = false,
  fluid = false,
  name,
  outlined = false,
  current = false,
  divider = false,
  shadow = false,
  shape = GroupedElementsShape.SQUARE,
  size = IconButtonSize.MEDIUM,
}: GroupedElementsProps) => (
  <div
    className={AttrsHelper.formatClassname(
      'grouped-elements',
      `grouped-elements-${direction}`,
      disableHover && 'grouped-elements-no-hover',
      fluid && 'grouped-elements-fluid',
      outlined && 'grouped-elements-outlined',
      current && 'grouped-elements-current',
      divider && 'grouped-elements-divider',
      shadow && 'grouped-elements-shadow',
      shape !== GroupedElementsShape.SQUARE && `grouped-elements-${shape}`,
      size !== IconButtonSize.MEDIUM && `grouped-elements-size-${size}`
    )}
    data-test={name}
  >
    {asItems(children, size)}
  </div>
);
